import { memo, useCallback } from 'react';
import { theme } from './constants';

const BUTTON_PRESETS = [
    { hex: '#FFFFFF', name: 'White' },
    { hex: '#3b82f6', name: 'Blue' },
    { hex: '#10b981', name: 'Green' },
    { hex: '#ef4444', name: 'Red' },
    { hex: '#000000', name: 'Black' },
    { hex: '#eab308', name: 'Yellow' }
];

const OptionsSidebar = ({
    isOpen,
    selectedSlot,
    config,
    componentOptions,
    setHoveredInnerVariant,
    toggleInnerComponent,
    setConfig,
    setIsOptionsOpen,
    setSelectedSlot,
    cameraTargetPos,
    controlsTargetLookAt,
    isAutoMoving,
    buttonColors,
    setButtonColors
}) => {

    const handleClose = useCallback(() => {
        setIsOptionsOpen(false);
        setSelectedSlot(null);
        setHoveredInnerVariant(null);
        isAutoMoving.current = true;
        cameraTargetPos.current.set(6, 0.5, 1.9);
        controlsTargetLookAt.current.set(0, 0, 0);
    }, [
        setIsOptionsOpen,
        setSelectedSlot,
        setHoveredInnerVariant,
        isAutoMoving,
        cameraTargetPos,
        controlsTargetLookAt
    ]);

    const handleColorClick = (e, colorHex) => {
        e.stopPropagation(); // Stop bubbling or the card click re-trigger s
        setButtonColors(prev => ({ ...prev, [selectedSlot]: colorHex }));
    };

    if (!selectedSlot) return null;

    return (
        <div style={{
            width: '320px',
            background: 'white',
            borderLeft: `1px solid ${theme.border}`,
            padding: '90px 25px 25px 25px',
            transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
            pointerEvents: 'auto'
        }}>
            <div style={{ fontSize: '11px', fontWeight: 900, color: theme.primary, letterSpacing: '1px' }}>
                {selectedSlot === 'inner_components' ? 'INTERNAL HARDWARE' : `SLOT ${selectedSlot}`}
            </div>

            <h3 style={{ margin: '5px 0 10px 0', fontWeight: 700 }}>{componentOptions[selectedSlot].label}</h3>

            <div className="custom-scrollbar" style={{ display: 'grid', gap: '10px', maxHeight: '60vh', overflowY: 'auto', paddingRight: '5px' }}>
                {componentOptions[selectedSlot].options.map((opt, idx) => {
                    const isSelected = selectedSlot === 'inner_components'
                        ? config.inner_components.some(i => i.variant === opt.variant)
                        : (config[selectedSlot].variant === opt.variant && config[selectedSlot].name === opt.name);

                    // Check if this option is a button
                    const isButton = opt.variant.includes('push_button');

                    return (
                        <div
                            key={`${opt.variant}-${idx}`}
                            onMouseEnter={() => selectedSlot === 'inner_components' && setHoveredInnerVariant(opt.variant)}
                            onMouseLeave={() => setHoveredInnerVariant(null)}
                            onClick={() => selectedSlot === 'inner_components' ? toggleInnerComponent(opt) : setConfig({ ...config, [selectedSlot]: opt })}
                            style={{
                                padding: '15px',
                                borderRadius: '10px',
                                border: `2px solid ${isSelected ? 'rgb(0, 40, 70)' : theme.border}`,
                                cursor: 'pointer',
                                background: 'rgb(255, 215, 0)',
                                // Changed layout to column to stack color options below
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px'
                            }}
                        >
                            {/* Name and Checkbox */}
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: 600 }}>{opt.name}</span>
                                    {opt.code && <span style={{ fontSize: '11px', color: theme.textMuted }}>Code: {opt.code}</span>}
                                </div>
                                <div style={{
                                    width: '18px', height: '18px',
                                    borderRadius: (selectedSlot === 'inner_components') ? '4px' : '50%',
                                    border: `1px solid ${theme.border}`,
                                    background: isSelected ? 'rgb(0, 40, 70)' : 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {isSelected && <div style={{ width: '6px', height: '6px', borderRadius: (selectedSlot === 'inner_components') ? '1px' : '50%', background: 'white' }} />}
                                </div>
                            </div>

                            {/* Color Options */}
                            {isButton && isSelected && (
                                <div style={{
                                    borderTop: '1px solid rgba(0,40,70,0.1)',
                                    paddingTop: '10px',
                                    marginTop: '5px'
                                }}>
                                    <div style={{ fontSize: '10px', fontWeight: 800, color: '#002846', marginBottom: '8px', textTransform: 'uppercase' }}>
                                        LENS COLOR
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {BUTTON_PRESETS.map((color) => {
                                            const currentColor = buttonColors[selectedSlot] || '#FFFFFF';
                                            const isColorActive = currentColor === color.hex;
                                            return (
                                                <div
                                                    key={color.name}
                                                    onClick={(e) => handleColorClick(e, color.hex)}
                                                    title={color.name}
                                                    style={{
                                                        width: '24px',
                                                        height: '24px',
                                                        backgroundColor: color.hex,
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        border: isColorActive ? '2px solid #002846' : '1px solid rgba(0,0,0,0.2)',
                                                        transform: isColorActive ? 'scale(1.1)' : 'scale(1)',
                                                        transition: 'transform 0.1s'
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <button
                onClick={handleClose}
                style={{ marginTop: '30px', width: '100%', padding: '12px', background: 'rgb(0, 40, 70)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, letterSpacing: '0.5px' }}
            >
                CONFIRM & CLOSE
            </button>
        </div>
    );
};

export default memo(OptionsSidebar);